
import { Enums } from '@bode-canada/core';
import { ToastMessage } from '@/ui-kit/components/toast/enums';
import api from '@/utils/api';
import ListingCard from '@/components/listing-card';
import Modal from '@/components/modal/index';
import WindowSize from '@/ui-kit/mixins/windowSize';
import {
  FREQUENCY_OPTIONS,
} from '@/constants/ListingSearchConstants';
import {
  FINISHED_AREA_OPTIONS,
  LAND_SIZE_OPTIONS,
  YEAR_BUILT_OPTIONS,
  PRICE_RANGE_OPTIONS,
  MIN_PRICE,
  MAX_PRICE,
  MIN_YEAR_BUILT,
  MIN_LAND_SIZE,
  MIN_FINISHED_AREA,
  MAX_LAND_SIZE,
  MAX_FINISHED_AREA,
  MAX_YEAR_BUILT,
} from '@/components/filters/constants/options';
import BuildingClassMapper from '@/components/features/search/shared/mappers/BuildingClassMapper';
import { SEARCH_BUILDING_CLASSES } from '@/components/features/search/shared/constants/BuildingClasses';
import { track } from '@/utils/analytics';
import { AUTHENTICATE, getAction } from '@/store/user/actions';
import routes from '@/constants/routes';
import headMeta from '@/constants/headMeta';
import SearchTopPanel from '@/components/page-specific/search/top-panel';
import ToastService from '@/components/toast/toast-service';
import {
  ViewListingAction,
  ViewSoldPropertyAction,
} from '@/components/listing-card/helpers/actions';
import { SearchPageEvent } from '@/constants/AnalyticEvents';
import { StyleColorSchemeType, StyleSizeType } from '@/ui-kit/styles/enums';
import { ALBERTA } from '@/components/maps/constants';
import { IconType } from '@/ui-kit/components/icon/enums';
import StyleClassNamesBuilder from '@/ui-kit/styles/helpers/StyleClassNamesBuilder';
import ViewSwitcher from '@/components/page-specific/search/view-switcher';
import ListingsMaps from '@/components/maps/entities/searchListings';
import GeolocationService from '@/components/maps/servises/GeolocationService';
import { MapZoomLevel } from '@/components/maps/enums';
import { QueryParams } from '@/constants/QueryParams';
import Spinner from '@/components/spinner';
import { DEFAULT_SEARCH_DATA } from '@/components/page-specific/search/constants';
import { FilterType } from '@/components/filters/enums';
import Button from '@/ui-kit/components/button';
import SearchApiService from '@/services/api/serverless/search';
import FilterMapper from '@/components/features/listing-search/mappers/filter';
import ListingMapper from '@/components/features/listing-search/mappers/listing';

const { ListingStatusEnum, ListingOriginEnum } = Enums;
const MIN_SEARCH_STRING_LENGTH = 3;

export default {
  name: 'ViewListings',

  components: {
    Spinner,
    ViewSwitcher,
    ListingsMaps,
    ListingCard,
    Modal,
    SearchTopPanel,
    Button,
  },

  mixins: [WindowSize],

  data() {
    const data = {
      showMapSpinner: false,
      isFiltersFromGeolocation: false,
      meta: {
        title: '',
        description: '',
      },
      isEditMode: false,
      listings: new Map(),
      tooManyResults: false,
      isTextSearching: false,
      textSearchString: '',
      textSearchSelectedOptionValue: null,
      textSearchOptions: [],
      mapSearching: false,
      searchData: { ...DEFAULT_SEARCH_DATA },
      filters: {},
      savedSearchName: '',
      savedSearchNotificationFreq: '',
      currentActiveQueries: {},
      isFiltersSettingsLoad: false,
      selectedPropertyTypes: SEARCH_BUILDING_CLASSES,
      priceRange: PRICE_RANGE_OPTIONS,
      landSizeOptions: LAND_SIZE_OPTIONS,
      finishedAreaOptions: FINISHED_AREA_OPTIONS,
      yearBuiltOptions: YEAR_BUILT_OPTIONS,
      filterActive: false,
      mapActive: false,
      searchSaveError: null,
      searchSaved: false,
      isSaveSearchLoading: false,
      savedSearchDialog: false,
      totalMatches: null,
      filterHeight: '100px',
      freqOptions: FREQUENCY_OPTIONS,
      formValid: true,
      fields: {
        uid: null,
      },
      promoFields: {
        title: null,
        text: null,
        buttonText: null,
      },
      pagination: {
        rowsPerPage: 6,
        page: 1,
      },
      sortByList: [
        { text: 'Highest to lowest', value: 'price_desc' },
        { text: 'Lowest to highest', value: 'price_asc' },
        { text: 'Recently updated', value: 'price_updated_at_desc' },
      ],
      previousPage: 0,
      StyleColorSchemeType,
      StyleSizeType,
      IconType,
    };

    return data;
  },
  async fetch() {
    await Promise.all([
      this.getContent(),
    ]);
  },
  head() {
    return {
      title: this.meta.title,
      meta: [
        { hid: 'description', name: 'description', content: this.meta.description },
        { property: 'og:title', content: this.meta.title },
        { property: 'og:description', content: this.meta.description },
        { property: 'og:url', content: `${headMeta.domain}${routes.search}` },
        { property: 'og:site_name', content: headMeta.siteName },
        { property: 'og:type', content: headMeta.type },
        { property: 'og:image', content: this.meta.image?.url },
      ],
    };
  },
  computed: {
    saveSearchButtonText() {
      return this.isEditMode ? 'Update search' : 'Save Search';
    },
    listingsArray() {
      const listings = [...this.listings.values()];

      return listings;
    },
    user() {
      return this.$store.state.user.userData;
    },
    limitReached() {
      return this.pagination.rowsPerPage >= this.totalMatches;
    },
    mapContainerClasses() {
      return new StyleClassNamesBuilder('map-container')
        .addConditionClassName('active', this.mapActive)
        .build();
    },
    viewSwitcherClasses() {
      return new StyleClassNamesBuilder('search-view-switcher')
        .addConditionClassName('map-view', this.mapActive)
        .build();
    },
    viewSwitcherButtons() {
      return [
        {
          title: this.saveSearchButtonText,
          callback: this.handleSaveSearchClick,
        },
        {
          title: this.mapActive ? 'Listings' : 'Map',
          callback: this.toggleMap,
        },
      ];
    },
    isMobileScreenSize() {
      return this.isSmallerThan(StyleSizeType.Small);
    },
  },

  watch: {
    searchData: {
      handler(value) {
        this.updateFiltersFromSearchData(value);
      },
      deep: true,
    },
  },

  async mounted() {
    this.isFiltersSettingsLoad = false;
    this.currentActiveQueries = this.$route.query;
    await GeolocationService.init();
    await this.initSearchData();
    const isFirefox = typeof InstallTrigger !== 'undefined';

    if (isFirefox) {
      document.getElementById('listings').classList.add('firefox');
      document.getElementById('search-filter-desktop').classList.add('firefox');
    }

    window.openListing = (listingId, origin) => {
      track('Search Listings_View listing selected', {
        listingId,
        listingType: origin,
      });
      window.open(`/listings/${listingId}`, '_blank');
    };

    if (this.isMobileScreenSize) {
      this.handleDefaultMobileFunctionality();
    }

    this.isFiltersSettingsLoad = true;
  },

  methods: {
    async getContent() {
      const { data } = await this.$prismic.client.getSingle('buy');
      if (data) {
        this.meta.title = data.meta_title;
        this.meta.description = data.meta_description;
        this.meta.image = data.meta_image;
      }
      return this.$prismic.client.getSingle('promo_tile').then((document) => {
        this.fields.uid = document.data.uid;
        this.promoFields.title = document.data.promo_title;
        this.promoFields.text = document.data.promo_text;
        this.promoFields.buttonText = document.data.promo_button_text;
      });
    },
    async loadSearchDataCenter() {
      try {
        const center = await GeolocationService.getDeviceGeolocation();
        const currentProvince = await GeolocationService.reverseGeoCode(
          center.lat,
          center.lng,
        );

        this.handleDefaultListingTypeFilter(currentProvince);

        return center;
      } catch {
        this.handleDefaultListingTypeFilter();
        return ALBERTA;
      }
    },
    handleDefaultListingTypeFilter(currentProvince = Enums.ProvinceEnum.AB) {
      if (currentProvince === Enums.ProvinceEnum.AB) {
        this.searchData.listingType.push(Enums.ListingOriginEnum.BODE);
      }
    },
    handleDefaultMobileFunctionality() {
      ToastService.openNeutral(ToastMessage.MobileSearchLocationServicesMessage);

      this.toggleMap();
    },
    loadSearchDataFromQueryParameters() {
      Object.keys(this.$route.query).forEach((param) => {
        const value = this.$route.query[param];

        switch (param) {
          case QueryParams.Edit: {
            this.isEditMode = value;

            return;
          }
          case QueryParams.Location: {
            this.searchData.location = value || '';
            this.textSearchString = value || '';

            return;
          }
          case QueryParams.Lat: {
            this.searchData.center = this.searchData.center || {};
            this.searchData.center.lat = parseFloat(value);

            return;
          }
          case QueryParams.Lng: {
            this.searchData.center = this.searchData.center || {};
            this.searchData.center.lng = parseFloat(value);

            return;
          }
          case QueryParams.MinLon: {
            const maxLon = this.$route.query[QueryParams.MaxLon];
            if (maxLon) {
              this.searchData.center = this.searchData.center || {};
              this.searchData.center.lng = parseFloat((Number(value) + Number(maxLon)) / 2);
            }

            return;
          }
          case QueryParams.MinLat: {
            const maxLat = this.$route.query[QueryParams.MaxLat];
            if (maxLat) {
              this.searchData.center = this.searchData.center || {};
              this.searchData.center.lat = parseFloat((Number(value) + Number(maxLat)) / 2);
            }

            return;
          }
          case QueryParams.Zoom: {
            this.searchData.zoom = parseInt(value, 10);

            return;
          }
          case QueryParams.Name: {
            this.savedSearchName = value;

            return;
          }
          case QueryParams.NotificationFreq: {
            this.savedSearchNotificationFreq = value;

            return;
          }
          case QueryParams.BuildingClass:
          case QueryParams.ListingType:
          case QueryParams.ListingStatus: {
            this.searchData[param] = value
              ? value.split(',')
              : this.searchData[param];

            return;
          }
          default: this.searchData[param] = value;
        }
      });
    },

    async initSearchData() {
      const isRouterQueries = Object.keys(this.$route.query).length;

      this.initZoom();

      if (isRouterQueries) {
        this.loadSearchDataFromQueryParameters();
      } else {
        this.updateFiltersFromSearchData();
      }

      if (!this.searchData.center?.lat || !this.searchData.center?.lng) {
        this.searchData.center = await this.loadSearchDataCenter();
      }
    },

    initZoom() {
      this.searchData.zoom = MapZoomLevel.SmallMedium;
    },

    handleFiltersChange(filters) {
      this.searchData = this.getSearchDataFromFilters(filters);
      this.addFiltersToQueryParams(filters);
      this.doMapSearch();
    },

    addFiltersToQueryParams(filters) {
      this.addQueryParams({
        [QueryParams.Location]: filters[FilterType.Location],
        [QueryParams.BuildingClass]: filters[FilterType.BuildingClass],
        [QueryParams.Beds]: filters[FilterType.Beds],
        [QueryParams.Baths]: filters[FilterType.Baths],
        [QueryParams.MinPrice]: filters[FilterType.Price].min,
        [QueryParams.MaxPrice]: filters[FilterType.Price].max,
        [QueryParams.ListingType]: filters[FilterType.ListingType],
        [QueryParams.ListingStatus]: filters[FilterType.ListingStatus],
        [QueryParams.MinFinishedSqft]: filters[FilterType.FinishedArea].min,
        [QueryParams.MaxFinishedSqft]: filters[FilterType.FinishedArea].max,
        [QueryParams.MinLotSizeAcres]: filters[FilterType.LandSize].min,
        [QueryParams.MaxLotSizeAcres]: filters[FilterType.LandSize].max,
        [QueryParams.MinYearBuilt]: filters[FilterType.YearBuilt].min,
        [QueryParams.MaxYearBuilt]: filters[FilterType.YearBuilt].max,
      });
    },

    addLocationToQueryParams({ lat, lng }) {
      this.addQueryParams({
        [QueryParams.Lat]: lat,
        [QueryParams.Lng]: lng,
      });
    },

    getFiltersFromSearchData() {
      return {
        [FilterType.Location]: this.searchData.location,
        [FilterType.BuildingClass]: this.searchData.buildingClass,
        [FilterType.Beds]: Number(this.searchData.beds),
        [FilterType.Baths]: Number(this.searchData.baths),
        [FilterType.Price]: {
          min: Number(this.searchData.minPrice),
          max: Number(this.searchData.maxPrice),
        },
        [FilterType.ListingType]: this.searchData.listingType,
        [FilterType.ListingStatus]: this.searchData.listingStatus,
        [FilterType.FinishedArea]: {
          min: Number(this.searchData.minFinishedSqft),
          max: Number(this.searchData.maxFinishedSqft),
        },
        [FilterType.LandSize]: {
          min: Number(this.searchData.minLotSizeAcres),
          max: Number(this.searchData.maxLotSizeAcres),
        },
        [FilterType.YearBuilt]: {
          min: Number(this.searchData.minYearBuilt),
          max: Number(this.searchData.maxYearBuilt),
        },
      };
    },

    getSearchDataFromFilters(filters) {
      return {
        ...this.searchData,
        beds: filters[FilterType.Beds],
        baths: filters[FilterType.Baths],
        minPrice: filters[FilterType.Price].min,
        maxPrice: filters[FilterType.Price].max,
        listingType: filters[FilterType.ListingType],
        listingStatus: filters[FilterType.ListingStatus],
        minFinishedSqft: filters[FilterType.FinishedArea].min,
        maxFinishedSqft: filters[FilterType.FinishedArea].max,
        minLotSizeAcres: filters[FilterType.LandSize].min,
        maxLotSizeAcres: filters[FilterType.LandSize].max,
        minYearBuilt: filters[FilterType.YearBuilt].min,
        maxYearBuilt: filters[FilterType.YearBuilt].max,
        location: filters[FilterType.Location],
        buildingClass: filters[FilterType.BuildingClass],
      };
    },

    updateFiltersFromSearchData(searchData) {
      const newFilters = this.getFiltersFromSearchData(searchData);
      this.filters = newFilters;
      this.addFiltersToQueryParams(newFilters);
    },

    scrollToTop() {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },

    async doMapSearch(newPositionData) {
      if (newPositionData?.bounds) {
        const { bounds } = newPositionData;
        const northEast = bounds.getNorthEast();
        const southWest = bounds.getSouthWest();

        this.searchData.minLat = southWest.lat();
        this.searchData.minLon = southWest.lng();
        this.searchData.maxLat = northEast.lat();
        this.searchData.maxLon = northEast.lng();
        this.searchData.center = newPositionData.center;
        this.searchData.zoom = newPositionData.zoom;

        this.addQueryParams({ [QueryParams.Zoom]: this.searchData.zoom });
        this.addLocationToQueryParams(this.searchData.center);
      }

      await this.searchListings();
    },
    async searchListings() {
      if (this.mapSearching || !this.searchData.minLat) return;

      this.showMapSpinner = true;
      this.mapSearching = true;

      const query = this.makeQuery(this.searchData);
      const queryString = this.toQueryString(query);
      try {
        if (queryString === this.lastQueryString) return;
        this.lastQueryString = queryString;
        const res = await SearchApiService.searchActiveListings(FilterMapper.mapToDto(query));
        if (res.tooManyResults) {
          ToastService.openNegative('There are too many results. Please zoom-in');
          this.tooManyResults = true;
          this.cachedQuery = null;
          this.totalMatches = null;
        } else {
          this.tooManyResults = false;
          this.cachedQuery = query;
          this.totalMatches = res.totalItems;
        }

        this.listings = new Map();

        if (res.data) {
          this.listings = new Map(res.data.map((listing) => [
            listing.id,
            ListingMapper.mapFromDto(listing)]));
        }

        const curQueryString = this.toQueryString(this.makeQuery(this.searchData));
        if (curQueryString !== queryString) {
          this.mapSearching = false;
          this.doMapSearch();
        }
      } finally {
        this.showMapSpinner = false;
        this.mapSearching = false;
      }
    },
    async handleSearchAutocompleteSelect({ value, text }) {
      if (value) {
        if (value.startsWith('listingId_')) {
          window.open(`/listings/${value.replace('listingId_', '')}`, '_blank');
        } else {
          const newCenter = await GeolocationService.getPlaceIdLocation(value);
          const lng = newCenter.lng();
          const lat = newCenter.lat();

          this.textSearchString = text;
          this.searchData.location = this.textSearchString;
          this.searchData.center = { lng, lat };
          this.searchData.zoom = MapZoomLevel.Small;
        }
      }
    },
    async handleSearchAutocompleteChange(textSearch) {
      this.textSearchString = textSearch;

      if (textSearch?.length < MIN_SEARCH_STRING_LENGTH) {
        this.textSearchOptions = [];
        return;
      }
      this.isTextSearching = true;
      this.searchData.location = this.textSearchString;

      try {
        const placePredictions = await this.loadGooglePlacesPredictionsByTextSearch(textSearch);
        const listingPredictions = await this.loadListingPredictionsFromTextSearch(textSearch);

        this.textSearchOptions = [...placePredictions, ...listingPredictions];
      } catch {
        this.textSearchOptions = [];
      } finally {
        this.isTextSearching = false;
      }
    },

    async loadGooglePlacesPredictionsByTextSearch(textSearch) {
      try {
        const places = await GeolocationService.searchPlacesByText(textSearch);

        return places.filter((place) => (
          place.types
          && place.types.includes('political')
          && place.terms
          && place.terms.find(({ value }) => value === 'Canada')
        ))
          .map((place) => ({
            text: place.description,
            value: place.place_id,
          }));
      } catch {
        return [];
      }
    },

    async loadListingPredictionsFromTextSearch(textSearch) {
      try {
        const { data } = await SearchApiService.searchActiveListings(FilterMapper.mapToDto({
          search: textSearch,
          pageSize: 20,
        }));

        return data.map(({ address, id }) => ({
          text: ListingMapper.getFullAddress(address),
          value: `listingId_${id}`,
        }));
      } catch {
        return [];
      }
    },

    handleSaveSearchClick() {
      this.openSaveSearchDialog();
    },

    async openSaveSearchDialog() {
      this.trackSavedSearchStartButtonClicked();
      if (!this.user.isAuthenticated) {
        await this.$store.dispatch(getAction(AUTHENTICATE));
      }

      this.savedSearchDialog = true;
    },

    async saveSearchForm() {
      this.isSaveSearchLoading = true;

      try {
        if (this.$refs.saveSearchForm.validate()) {
          await this.saveSearch();
          this.savedSearchDialog = false;
          ToastService.openPositive('Search has been saved');
        }
      } catch {
        ToastService.openNegative('Search has not been saved');
      } finally {
        this.isSaveSearchLoading = false;
      }
    },

    async saveSearch() {
      const query = {
        ...this.makeQuery(this.searchData),
        name: this.savedSearchName,
        notificationFreq: this.savedSearchNotificationFreq,
      };
      let res;

      if (this.isEditMode) {
        res = await api.put(`/listings/saved-searches/${this.searchData.searchId}`, {
          query,
        });
      } else {
        res = await api.post('/listings/saved-searches', {
          query,
        });
      }
      if (res.success) {
        this.$router.push('/search/saved-search-list');
      } else {
        ToastService.openNegative(this.searchSaveError);
        this.searchSaveError = res.error;
      }
    },

    getQueryMaxMinField(value, minOrMaxValue) {
      return Number(value) === minOrMaxValue ? '' : value;
    },

    makeQuery(searchData) {
      return {
        minLat: searchData.minLat,
        minLon: searchData.minLon,
        maxLat: searchData.maxLat,
        maxLon: searchData.maxLon,
        minBeds: searchData.beds,
        minBaths: searchData.baths,
        showBodeSales: searchData.listingStatus.length ? ListingStatusEnum.SOLD : '',
        minPrice: this.getQueryMaxMinField(searchData.minPrice, MIN_PRICE),
        maxPrice: this.getQueryMaxMinField(searchData.maxPrice, MAX_PRICE),
        minYearBuilt: this.getQueryMaxMinField(searchData.minYearBuilt, MIN_YEAR_BUILT),
        maxYearBuilt: this.getQueryMaxMinField(searchData.maxYearBuilt, MAX_YEAR_BUILT),
        minLotSizeAcres: this.getQueryMaxMinField(searchData.minLotSizeAcres, MIN_LAND_SIZE),
        maxLotSizeAcres: this.getQueryMaxMinField(searchData.maxLotSizeAcres, MAX_LAND_SIZE),
        minFinishedSqft: this.getQueryMaxMinField(searchData.minFinishedSqft, MIN_FINISHED_AREA),
        maxFinishedSqft: this.getQueryMaxMinField(searchData.maxFinishedSqft, MAX_FINISHED_AREA),
        origin: searchData.listingType?.length ? ListingOriginEnum.BODE : '',
        searchString: '',
        limit: 240,
        overLimit: true,
        group: true,
        sort: searchData.sort,
        location: searchData.location,
        ...BuildingClassMapper.mapFilterToQuery(searchData.buildingClass || []),
      };
    },

    toQueryString(query) {
      return Object.keys(query)
        .map((key) => `${key}=${query[key]}`)
        .join('&');
    },

    filterResize() {
      if (window.innerWidth >= '960') {
        this.filterHeight = '100px';
      } else {
        this.filterHeight = '200px';
      }
    },

    toggleMap() {
      this.trackMapClickMobile();
      this.mapActive = !this.mapActive;
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },

    addRows() {
      this.pagination.rowsPerPage += 6;
    },

    async handleResetFilters() {
      const newCenter = await this.loadSearchDataCenter();
      const currentCenter = this.searchData.center;
      const defaultValues = this.isEditMode
        ? {
          ...DEFAULT_SEARCH_DATA,
          center: newCenter,
          searchId: this.searchData.searchId,
          location: this.searchData.location,
        }
        : { ...DEFAULT_SEARCH_DATA, center: newCenter };

      if (newCenter.lng !== currentCenter.lng || newCenter.lat !== currentCenter.lat) {
        this.searchData = defaultValues;

        return;
      }

      this.searchData = {
        ...defaultValues,
        minLat: this.searchData.minLat,
        minLon: this.searchData.minLon,
        maxLat: this.searchData.maxLat,
        maxLon: this.searchData.maxLon,
      };

      await this.searchListings();
    },

    async handleListingSort() {
      await this.searchListings();
      this.trackSortBySelection();
    },

    // #region tracking

    /**
     * @track Tracks the clicking of the promo tile in Mixpanel (via Segment).
     *
     * @returns {void}
     * @private
     */
    trackResultsFilteredForBodeListingsUsingTile() {
      track('Search Listings_Results filtered for Bōde listings using tile');
    },

    /**
     * @track Tracks the clicking of the Saved Search button in Mixpanel (via Segment).
     *
     * @returns {void}
     * @private
     */
    trackSavedSearchStartButtonClicked() {
      track('Search Listings_Saved search start button clicked');
    },

    /**
     * @track Tracks the naming of a saved search in Mixpanel (via Segment).
     *
     * @returns {void}
     * @private
     */
    trackSavedSearchNameEntered(blurEvent) {
      track('Search Listings_Saved search name entered', {
        'Saved search name': blurEvent.target.value,
      });
    },

    /**
     * @track Tracks the selecting of saved search frequency button in Mixpanel (via Segment).
     *
     * @returns {void}
     * @private
     */
    trackSavedSearchFrequencySelected(frequency) {
      track('Search Listings_Saved search frequency selected', {
        'Saved search frequency': frequency,
      });
    },

    /**
     * @track Tracks the clicking of the Save search modal button in Mixpanel (via Segment).
     *
     * @returns {void}
     * @private
     */
    trackSavedSearchCompleteButtonClicked() {
      track('Search Listings_Saved search complete button clicked');
    },

    /**
     * @track Tracks the selecting of the sorting order in Mixpanel (via Segment).
     *
     * @returns {void}
     * @private
     */
    trackSortBySelection() {
      const sortItem = this.searchData.sort;
      if (sortItem === 'price_asc') {
        track('Search Listings_Search results filtered from lowest to highest');
      } else if (sortItem === 'price_desc') {
        track('Search Listings_Search results filtered from highest to lowest');
      } else {
        track(
          'Search Listings_Search results filtered by most recently updated',
        );
      }
    },

    /**
     * @track Tracks the clicking on the next or previous buttons in the pagination footer in
     * Mixpanel (via Segment).
     *
     * @returns {void}
     * @private
     */
    trackNextOrPreviousListingsLoaded() {
      const currentPage = this.pagination.page;

      // If the "Listings per page" dropdown is clicked,
      // current page and previous page will be the same.
      // Also when the page loads,
      // currentPage will be 1 and previousPage will be 0.
      // We're not tracking these:
      if (
        currentPage === this.previousPage
        || (this.previousPage === 0 && currentPage === 1)
      ) {
        return;
      }

      const nextClicked = this.previousPage < currentPage;
      this.previousPage = currentPage;

      track(
        `Search Listings_${
          nextClicked ? 'Next' : 'Previous'
        } page of listings loaded`,
      );
    },

    /**
     * @track Tracks the clicking of the More results button (mobile) in Mixpanel (via Segment).
     *
     * @returns {void}
     * @private
     */

    trackMoreResultsButtonClicked() {
      track('Search Listings_More results button clicked');
    },

    /**
     * @track Tracks the clicking of the Back to top button (mobile) in Mixpanel (via Segment).
     *
     * @returns {void}
     * @private
     */

    trackBackToTopButtonClicked() {
      track('Search Listings_Back to top button Clicked');
    },

    /**
     * @track Tracks the clicking of the mobile 'Map' button in Mixpanel (via Segment).
     *
     * @returns {void}
     * @private
     */

    trackMapClickMobile() {
      track('Search Listings_Map button click in mobile view');
    },

    addQueryParams(params) {
      const query = {
        ...this.currentActiveQueries,
        ...params,
      };

      this.currentActiveQueries = query;

      const search = Object
        .keys(query)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
        .join('&');

      window.history.pushState({}, null, `${this.$route.path}?${search}`);
    },
    addQueryParam(parameterName, value) {
      this.addQueryParams({ [parameterName]: value.toString() });
    },
    getViewListingAction() {
      return new ViewListingAction({ analyticEvent: SearchPageEvent.ViewListing });
    },
    getViewSoldPropertyAction() {
      return new ViewSoldPropertyAction({ analyticEvent: SearchPageEvent.ViewListing });
    },
    isSold(listing) {
      return listing.status === ListingStatusEnum.SOLD;
    },
  },
};
